import { useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    FormControlLabel,
    FormHelperText,
    IconButton,
    InputAdornment,
    MenuItem,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import PropTypes from 'prop-types';

import Iconify from '../../../components/iconify';
import CodeEditor from '../../scraper/CodeEditor';
import JsonObjectEditor from './JsonObjectEditor';
import ScraperActionInput from './ScraperActionInput';
import {
    deprecatedKeys,
    emptyItem,
    getAt,
    humanDuration,
    isAbsent,
    isMask,
    parseInteger,
    parseNumber,
    pathText,
    problemsByPath,
    unknownKeys
} from '../../../utils/gatewaySettings';
import i18n from '../../../i18n';

const shownDefault = (field) => {
    const value = field.defaultValue;
    if (value === undefined || value === null) return '';
    return typeof value === 'object' ? JSON.stringify(value) : String(value);
};

/**
 * A field's help: the cabinet's own words for it in this language
 * (`gatewaySettings.help.<api>.<kind>.<path>`, list indexes dropped) when it has
 * them, else the description core gives from the driver's settings class. The api
 * and kind are the settings row's: `tg_session.payin_callback`, not the api's name
 * before V28 (`tgbank`), under which no help was ever found.
 */
export const helpFor = (ctx, path, field) => {
    const key = `gatewaySettings.help.${ctx.api}.${ctx.kind}.${path.filter((step) => typeof step !== 'number').join('.')}`;
    // An object's key holds its children's help, and its own words, if any, under `_`.
    const own = i18n.t(key, { returnObjects: true, defaultValue: '' });
    const text = own !== null && typeof own === 'object' ? own._ : own;
    return typeof text === 'string' && text !== '' && text !== key ? text : field.description || '';
};

// A TextField draws its label twice, once floating and once (hidden) in the
// outline's notch, so there the label takes the input's size (`inherit`): a fixed
// body2 made the notch wider than the label and left a gap in the outline.
function FieldLabel({ field, path, variant = 'body2' }) {
    const last = path[path.length - 1];
    return (
        <Typography variant={variant} component="span" sx={{ fontFamily: 'monospace' }}>
            {typeof last === 'number' ? `#${last + 1}` : field.key}
            {field.required ? ' *' : ''}
        </Typography>
    );
}

FieldLabel.propTypes = {
    field: PropTypes.object.isRequired,
    path: PropTypes.array.isRequired,
    variant: PropTypes.string
};

/**
 * A secret, which the cabinet returns as its mask. Left alone the mask stays in the
 * value and the cabinet keeps the stored secret; whatever is typed replaces it.
 * Clearing the input brings the mask back; the bin removes the key.
 */
function SecretInput({ field, path, value, onChangeAt, ctx, common }) {
    const [mask] = useState(() => (isMask(value) ? value : null));
    const [visible, setVisible] = useState(false);
    const typed = isMask(value) || value === undefined || value === null ? '' : String(value);
    const removed = mask && (value === undefined || value === null);

    let note = common.helperText;
    if (!common.error) {
        if (removed) note = i18n.t('gatewaySettings.secretRemoved');
        else if (mask && typed) note = i18n.t('gatewaySettings.secretReplaced');
        else if (mask) note = i18n.t('gatewaySettings.secretStored', { mask });
    }

    return (
        <TextField
            {...common}
            helperText={note}
            type={visible ? 'text' : 'password'}
            value={typed}
            placeholder={removed ? '' : mask || ''}
            autoComplete="new-password"
            onChange={(event) => onChangeAt(path, event.target.value === '' ? mask ?? undefined : event.target.value)}
            InputProps={{
                endAdornment: (
                    <InputAdornment position="end">
                        {typed && (
                            <IconButton size="small" onClick={() => setVisible((v) => !v)} edge="end">
                                <Iconify icon={visible ? 'eva:eye-off-outline' : 'eva:eye-outline'} width={18} />
                            </IconButton>
                        )}
                        {mask && !removed && (
                            <Tooltip title={i18n.t('gatewaySettings.secretRemove')}>
                                <span>
                                    <IconButton size="small" disabled={ctx.disabled} aria-label={i18n.t('gatewaySettings.secretRemove')} onClick={() => onChangeAt(path, undefined)}>
                                        <Iconify icon="eva:trash-2-outline" width={18} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                        {removed && (
                            <Button size="small" disabled={ctx.disabled} onClick={() => onChangeAt(path, mask)}>
                                {i18n.t('gatewaySettings.undo')}
                            </Button>
                        )}
                    </InputAdornment>
                )
            }}
        />
    );
}

SecretInput.propTypes = {
    field: PropTypes.object.isRequired,
    path: PropTypes.array.isRequired,
    value: PropTypes.any,
    onChangeAt: PropTypes.func.isRequired,
    ctx: PropTypes.object.isRequired,
    common: PropTypes.object.isRequired
};

function Section({ field, path, ctx, onRemove, children }) {
    const help = helpFor(ctx, path, field);
    return (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
                <FieldLabel field={field} path={path} />
                <Box sx={{ flex: 1 }} />
                {onRemove && (
                    <Tooltip title={i18n.t('gatewaySettings.remove')}>
                        <span>
                            <IconButton size="small" disabled={ctx.disabled} aria-label={i18n.t('gatewaySettings.remove')} onClick={onRemove}>
                                <Iconify icon="eva:trash-2-outline" width={18} />
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
            </Stack>
            {help && (
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                    {help}
                </Typography>
            )}
            {children}
        </Box>
    );
}

Section.propTypes = {
    field: PropTypes.object.isRequired,
    path: PropTypes.array.isRequired,
    ctx: PropTypes.object.isRequired,
    onRemove: PropTypes.func,
    children: PropTypes.node
};

/** One key of the settings, by its type; objects and lists recurse. */
function SettingInput({ field, path, root, onChangeAt, ctx }) {
    const value = getAt(root, path);
    const id = `${ctx.idPrefix}-${pathText(path)}`;
    const problem = ctx.problems[pathText(path)];
    const shownProblem = problem && (ctx.showAll || ctx.touched.has(pathText(path))) ? problem : null;
    const help = helpFor(ctx, path, field);
    const common = {
        id,
        size: 'small',
        fullWidth: true,
        disabled: ctx.disabled,
        label: <FieldLabel field={field} path={path} variant="inherit" />,
        error: Boolean(shownProblem),
        helperText: shownProblem ? i18n.t(shownProblem.key, shownProblem.params) : help || ' ',
        onBlur: () => ctx.touch(pathText(path)),
        InputLabelProps: { shrink: true }
    };
    const placeholder = shownDefault(field);

    if (field.widget === 'scraper_action') {
        const inputProblem = ctx.problems[pathText([...path, 'input'])];
        return (
            <ScraperActionInput
                actionKey={field.key}
                action={value}
                onChange={(action) => onChangeAt(path, action)}
                onRemove={() => onChangeAt(path, undefined)}
                scenarios={ctx.scenarios}
                store={ctx.store}
                disabled={ctx.disabled}
                showCode
                editorPath={`${ctx.editorPrefix}/${pathText(path)}`}
                description={help}
                inputError={Boolean(inputProblem)}
                inputHelperText={inputProblem ? i18n.t(inputProblem.key, inputProblem.params) : undefined}
            />
        );
    }

    if (field.secret && (field.type === 'text' || isMask(value))) {
        return <SecretInput field={field} path={path} value={value} onChangeAt={onChangeAt} ctx={ctx} common={common} />;
    }

    switch (field.type) {
        case 'choice':
            return (
                <TextField
                    {...common}
                    select
                    value={typeof value === 'string' ? value : ''}
                    onChange={(event) => onChangeAt(path, event.target.value === '' ? undefined : event.target.value)}
                >
                    {!field.required && (
                        <MenuItem value="">
                            <em>{placeholder ? i18n.t('gatewaySettings.default', { value: placeholder }) : '—'}</em>
                        </MenuItem>
                    )}
                    {(field.options || []).map((option) => (
                        <MenuItem key={option} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </TextField>
            );
        case 'integer':
        case 'number':
            return (
                <TextField
                    {...common}
                    value={value === undefined || value === null ? '' : String(value)}
                    placeholder={placeholder}
                    inputProps={{ inputMode: field.type === 'integer' ? 'numeric' : 'decimal', autoComplete: 'off' }}
                    onChange={(event) =>
                        onChangeAt(path, (field.type === 'integer' ? parseInteger : parseNumber)(event.target.value))
                    }
                />
            );
        case 'boolean': {
            const effective = typeof value === 'boolean' ? value : field.defaultValue === true;
            const isDefault = typeof value !== 'boolean';
            return (
                <Box>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <FormControlLabel
                            sx={{ mr: 0 }}
                            control={
                                <Switch
                                    size="small"
                                    checked={effective}
                                    disabled={ctx.disabled}
                                    onChange={(event) => onChangeAt(path, event.target.checked)}
                                />
                            }
                            label={<FieldLabel field={field} path={path} />}
                        />
                        {isDefault ? (
                            <Chip size="small" variant="outlined" label={i18n.t('gatewaySettings.defaultChip')} />
                        ) : (
                            <Tooltip title={i18n.t('gatewaySettings.reset')}>
                                <span>
                                    <IconButton size="small" disabled={ctx.disabled} onClick={() => onChangeAt(path, undefined)}>
                                        <Iconify icon="eva:refresh-outline" width={16} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                    </Stack>
                    <FormHelperText error={Boolean(shownProblem)} sx={{ mt: 0 }}>
                        {common.helperText}
                    </FormHelperText>
                </Box>
            );
        }
        case 'duration': {
            const human = humanDuration(isAbsent(field, value) ? field.defaultValue : value);
            return (
                <TextField
                    {...common}
                    value={value === undefined || value === null ? '' : String(value)}
                    placeholder={placeholder || 'PT1M'}
                    inputProps={{ autoComplete: 'off' }}
                    InputProps={{
                        sx: { fontFamily: 'monospace' },
                        endAdornment: human ? (
                            <InputAdornment position="end">
                                <Typography variant="caption" color="text.secondary">
                                    {human}
                                </Typography>
                            </InputAdornment>
                        ) : undefined
                    }}
                    onChange={(event) => onChangeAt(path, event.target.value === '' ? undefined : event.target.value)}
                />
            );
        }
        case 'json':
            return (
                <Box>
                    <FieldLabel field={field} path={path} />
                    <JsonObjectEditor
                        value={value}
                        path={`${ctx.editorPrefix}/${pathText(path)}.json`}
                        disabled={ctx.disabled}
                        error={Boolean(shownProblem)}
                        helperText={common.helperText}
                        onChange={(next) => {
                            ctx.touch(pathText(path));
                            onChangeAt(path, next);
                        }}
                    />
                </Box>
            );
        case 'object':
            if (value === undefined || value === null) {
                return (
                    <Section field={field} path={path} ctx={ctx}>
                        <Button
                            size="small"
                            disabled={ctx.disabled}
                            startIcon={<Iconify icon="eva:plus-fill" width={16} />}
                            onClick={() => onChangeAt(path, {})}
                        >
                            {i18n.t('gatewaySettings.add')}
                        </Button>
                        {shownProblem && (
                            <FormHelperText error>{i18n.t(shownProblem.key, shownProblem.params)}</FormHelperText>
                        )}
                    </Section>
                );
            }
            return (
                <Section
                    field={field}
                    path={path}
                    ctx={ctx}
                    onRemove={field.required ? undefined : () => onChangeAt(path, undefined)}
                >
                    <Stack spacing={1.5} sx={{ pt: 1 }}>
                        {(field.fields || []).filter((child) => !child.deprecated).map((child) => (
                            <SettingInput
                                key={child.key}
                                field={child}
                                path={[...path, child.key]}
                                root={root}
                                onChangeAt={onChangeAt}
                                ctx={ctx}
                            />
                        ))}
                    </Stack>
                </Section>
            );
        case 'list':
            return field.item?.type === 'object' ? (
                <ObjectList field={field} path={path} value={value} root={root} onChangeAt={onChangeAt} ctx={ctx} problem={shownProblem} />
            ) : (
                <Autocomplete
                    multiple
                    freeSolo
                    size="small"
                    options={[]}
                    disabled={ctx.disabled}
                    value={Array.isArray(value) ? value.map(String) : []}
                    onChange={(_event, items) => {
                        const parsed = items.map((item) =>
                            field.item?.type === 'integer' ? parseInteger(item) : field.item?.type === 'number' ? parseNumber(item) : item
                        );
                        onChangeAt(path, parsed.length === 0 && !field.required ? undefined : parsed);
                    }}
                    renderTags={(items, getTagProps) =>
                        items.map((item, index) => (
                            <Chip size="small" variant="outlined" label={item} {...getTagProps({ index })} key={item} />
                        ))
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={common.label}
                            error={common.error}
                            helperText={common.helperText}
                            placeholder={i18n.t('gatewaySettings.listHint')}
                            onBlur={common.onBlur}
                            InputLabelProps={{ shrink: true }}
                        />
                    )}
                />
            );
        case 'text':
        default:
            if (field.format === 'code') {
                return (
                    <Box>
                        <FieldLabel field={field} path={path} />
                        <CodeEditor
                            language="javascript"
                            path={`${ctx.editorPrefix}/${pathText(path)}.js`}
                            value={typeof value === 'string' ? value : ''}
                            readOnly={ctx.disabled}
                            onChange={(text) => onChangeAt(path, text === '' ? undefined : text)}
                            sx={{ height: 200 }}
                        />
                        <FormHelperText error={Boolean(shownProblem)}>{common.helperText}</FormHelperText>
                    </Box>
                );
            }
            return (
                <TextField
                    {...common}
                    value={value === undefined || value === null ? '' : typeof value === 'string' ? value : JSON.stringify(value)}
                    placeholder={placeholder || (field.format === 'url' ? 'https://' : '')}
                    inputProps={{ autoComplete: 'off', spellCheck: field.format === 'regex' ? false : undefined }}
                    InputProps={field.format === 'regex' || field.format === 'url' ? { sx: { fontFamily: 'monospace' } } : undefined}
                    onChange={(event) => onChangeAt(path, event.target.value === '' ? undefined : event.target.value)}
                />
            );
    }
}

SettingInput.propTypes = {
    field: PropTypes.object.isRequired,
    path: PropTypes.array.isRequired,
    root: PropTypes.any,
    onChangeAt: PropTypes.func.isRequired,
    ctx: PropTypes.object.isRequired
};

/** A list of objects — a tg_session row's incoming rules, say — as cards that can be added, moved and removed. */
function ObjectList({ field, path, value, root, onChangeAt, ctx, problem }) {
    const items = Array.isArray(value) ? value : [];
    const move = (from, to) => {
        const next = [...items];
        const [item] = next.splice(from, 1);
        next.splice(to, 0, item);
        onChangeAt(path, next);
    };
    const remove = (index) => {
        const next = items.filter((_item, i) => i !== index);
        onChangeAt(path, next.length === 0 && !field.required ? undefined : next);
    };

    return (
        <Section field={field} path={path} ctx={ctx}>
            <Stack spacing={1}>
                {items.map((item, index) => (
                    // Index keys: the elements have no identity of their own, and
                    // the inputs are controlled, so a move re-renders them in place.
                    // eslint-disable-next-line react/no-array-index-key
                    <Box key={index} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1, pt: 0.5 }}>
                        <Stack direction="row" alignItems="center">
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                #{index + 1}
                            </Typography>
                            <Box sx={{ flex: 1 }} />
                            <IconButton size="small" disabled={ctx.disabled || index === 0} onClick={() => move(index, index - 1)}>
                                <Iconify icon="eva:arrow-upward-outline" width={16} />
                            </IconButton>
                            <IconButton
                                size="small"
                                disabled={ctx.disabled || index === items.length - 1}
                                onClick={() => move(index, index + 1)}
                            >
                                <Iconify icon="eva:arrow-downward-outline" width={16} />
                            </IconButton>
                            <Tooltip title={i18n.t('gatewaySettings.remove')}>
                                <span>
                                    <IconButton size="small" disabled={ctx.disabled} aria-label={i18n.t('gatewaySettings.remove')} onClick={() => remove(index)}>
                                        <Iconify icon="eva:trash-2-outline" width={16} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Stack>
                        <Stack spacing={1.5}>
                            {(field.item.fields || []).filter((child) => !child.deprecated).map((child) => (
                                <SettingInput
                                    key={child.key}
                                    field={child}
                                    path={[...path, index, child.key]}
                                    root={root}
                                    onChangeAt={onChangeAt}
                                    ctx={ctx}
                                />
                            ))}
                        </Stack>
                    </Box>
                ))}
                <Box>
                    <Button
                        size="small"
                        disabled={ctx.disabled}
                        startIcon={<Iconify icon="eva:plus-fill" width={16} />}
                        onClick={() => onChangeAt(path, [...items, emptyItem(field.item)])}
                    >
                        {i18n.t('gatewaySettings.addItem')}
                    </Button>
                </Box>
                {problem && <FormHelperText error>{i18n.t(problem.key, problem.params)}</FormHelperText>}
            </Stack>
        </Section>
    );
}

ObjectList.propTypes = {
    field: PropTypes.object.isRequired,
    path: PropTypes.array.isRequired,
    value: PropTypes.any,
    root: PropTypes.any,
    onChangeAt: PropTypes.func.isRequired,
    ctx: PropTypes.object.isRequired,
    problem: PropTypes.object
};

/**
 * A gateway settings row's settings as the form of its driver's schema (utils/gatewaySettings):
 * one input per key the driver reads, in the order its settings class declares
 * them, each with the problem the cabinet would refuse it for.
 *
 * Keys the driver does not read are kept as they are and listed, with a button to
 * drop them; keys it refuses now (deprecated) are listed as an error, since the
 * cabinet will not save them.
 */
export default function GatewaySettingsForm({
    schema,
    value,
    problems,
    onChangeAt,
    onRemoveKeys,
    disabled,
    showAll,
    scenarios,
    store,
    idPrefix,
    editorPrefix
}) {
    const [touched, setTouched] = useState(() => new Set());
    const ctx = {
        api: schema.api,
        kind: schema.kind,
        disabled,
        showAll,
        scenarios,
        store,
        idPrefix,
        editorPrefix,
        problems: problemsByPath(problems),
        touched,
        touch: (path) => setTouched((current) => (current.has(path) ? current : new Set([...current, path])))
    };
    const unknown = unknownKeys(schema, value);
    const deprecated = deprecatedKeys(schema, value);

    return (
        <Stack spacing={1.5} sx={{ pt: 1 }}>
            {deprecated.length > 0 && (
                <Alert
                    severity="error"
                    variant="outlined"
                    action={
                        <Button color="inherit" size="small" disabled={disabled} onClick={() => onRemoveKeys(deprecated)}>
                            {i18n.t('gatewaySettings.removeKeys')}
                        </Button>
                    }
                >
                    {i18n.t('gatewaySettings.deprecatedKeys', { keys: deprecated.join(', ') })}
                </Alert>
            )}
            {schema.fields
                .filter((field) => !field.deprecated)
                .map((field) => (
                    <SettingInput key={field.key} field={field} path={[field.key]} root={value} onChangeAt={onChangeAt} ctx={ctx} />
                ))}
            {unknown.length > 0 && (
                <Alert
                    severity="info"
                    variant="outlined"
                    action={
                        <Button color="inherit" size="small" disabled={disabled} onClick={() => onRemoveKeys(unknown)}>
                            {i18n.t('gatewaySettings.removeKeys')}
                        </Button>
                    }
                >
                    {i18n.t('gatewaySettings.unknownKeys', { keys: unknown.join(', ') })}
                </Alert>
            )}
        </Stack>
    );
}

GatewaySettingsForm.propTypes = {
    schema: PropTypes.shape({ api: PropTypes.string, kind: PropTypes.string, fields: PropTypes.array }).isRequired,
    value: PropTypes.any,
    problems: PropTypes.array.isRequired,
    onChangeAt: PropTypes.func.isRequired,
    onRemoveKeys: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    showAll: PropTypes.bool,
    scenarios: PropTypes.array,
    store: PropTypes.object,
    idPrefix: PropTypes.string.isRequired,
    editorPrefix: PropTypes.string.isRequired
};
